import { Injectable } from '@angular/core'
import { environment } from '../../../environments/environment'
import { HttpClient, HttpErrorResponse } from '@angular/common/http'
import {
    EMPTY,
    forkJoin,
    map,
    MonoTypeOperatorFunction,
    Observable,
    throwError,
    of,
    switchMap,
    take,
} from 'rxjs'
import { catchError, tap } from 'rxjs/operators'
import { CaseId, MarkerDimension, SegObjectPropertyMap } from '../../state/case/case.state'
import { DimensionDataWithDomain } from '../../state/plot-data/plot-data.state'
import {
    ViewerConfigApiRequest,
    ViewerConfigApiResponse,
    ViewerState,
} from '../../state/viewer/viewer.state'
import {
    FreeSelection,
    FreeSelectionsForCase,
    generateSelectionId,
    getHierarchicalRootSegSources,
    HierarchicalSelectionNode,
    HierarchicalSelectionRoot,
    HierarchicalSelectionsForCase,
    SelectionBucket,
    SelectionBucketJson,
    SELECTION_SCHEMA_VERSION,
    SelectionType,
    UnsupervisedSelectionRoot,
} from '../../state/selection/selection.state'
import { mapViewerStateToApiRequest } from '../../state/viewer/viewer-state-utils'
import { Store } from '@ngrx/store'
import { concatLatestFrom } from '@ngrx/effects'
import { selectCaseId } from '../../state/case/case.selectors'
import { selectLayers, selectViewerState } from '../../state/viewer/viewer.selectors'

const PLOT_DATA_LOG = '[PlotData]'

@Injectable({
    providedIn: 'root',
})
export class DatasetApiService {
    private readonly baseUrl = environment.apiUrl
    private readonly publicUrl = environment.publicApiUrl

    constructor(
        private readonly http: HttpClient,
        private readonly store: Store
    ) {}

    getSegObjectPropertyMaps(caseId: CaseId): Observable<SegObjectPropertyMap[]> {
        return caseId
            ? this.http.get<SegObjectPropertyMap[]>(`${this.baseUrl}/datasets/${caseId}/mappings`)
            : EMPTY
    }

    getSegObjectPropertiesData(
        caseId: CaseId,
        segObjectPropertyMap: SegObjectPropertyMap
    ): Observable<MarkerDimension[]> {
        return segObjectPropertyMap
            ? this.http
                  .get<MarkerDimension[]>(
                      `${this.baseUrl}/datasets/${caseId}/mappings/${segObjectPropertyMap.id}`
                  )
                  .pipe(
                      map(markerDimensions =>
                          markerDimensions.map(marker => ({
                              ...marker,
                              segobjectpropertymap: segObjectPropertyMap, // Assign the segObjectPropertyMap to the new field
                          }))
                      )
                  )
            : EMPTY
    }

    getSegObjectPropertyMapJobSettings(
        caseId: CaseId,
        segObjectPropertyMap: SegObjectPropertyMap
    ): Observable<any> {
        const url = `${this.baseUrl}/datasets/${caseId}/segObjectPropertyMaps/${segObjectPropertyMap.name}/job_settings.json`
        return this.http.get<any>(url).pipe(catchError(() => EMPTY))
    }

    getMarkersForCase(caseId: CaseId): Observable<MarkerDimension[]> {
        return caseId
            ? this.http.get<MarkerDimension[]>(`${this.baseUrl}/datasets/${caseId}/markers`)
            : EMPTY
    }

    getViewerConfigForCase(caseId: CaseId, shared: boolean): Observable<ViewerConfigApiResponse> {
        const baseUrl = shared ? this.publicUrl : this.baseUrl
        return this.http.get<ViewerConfigApiResponse>(`${baseUrl}/datasets/${caseId}`)
    }

    getDataForDimension(marker: MarkerDimension): Observable<DimensionDataWithDomain> {
        console.log(PLOT_DATA_LOG, 'getDataForDimension HTTP', {
            marker: marker.name,
            source_bin: marker.source_bin,
            hasSourceJson: !!marker.source_json,
        })

        const json$ = marker.source_json
            ? this.http.get<any>(marker.source_json)
            : of({ legend: undefined })

        return this.http.get(marker.source_bin, { responseType: 'arraybuffer' }).pipe(
            tap(() =>
                console.log(PLOT_DATA_LOG, 'bin downloaded', {
                    marker: marker.name,
                    source_bin: marker.source_bin,
                })
            ),
            switchMap(binData =>
                json$.pipe(
                    tap(() =>
                        console.log(PLOT_DATA_LOG, 'json downloaded', {
                            marker: marker.name,
                            source_json: marker.source_json ?? '(skipped)',
                        })
                    ),
                    map(jsonData => ({ binData, jsonData }))
                )
            ),
                map(({ binData, jsonData }) => {
                    const dataView = new DataView(binData)
                    const bytesPerPoint = 8
                    const pointCount = binData.byteLength / bytesPerPoint
                    const points = []
                    for (let i = 0; i < pointCount; i++) {
                        const id = dataView.getUint32(i * bytesPerPoint, true)
                        const value = dataView.getFloat32(i * bytesPerPoint + 4, true)
                        points.push([id, value])
                    }
                    return {
                        domain: {
                            lowerBound: marker.lower_bound,
                            upperBound: marker.upper_bound,
                        },
                        points: points,
                        legend: jsonData.legend,
                    } as DimensionDataWithDomain
                }),
                catchError(error => {
                    console.error(PLOT_DATA_LOG, 'getDataForDimension failed', {
                        marker: marker.name,
                        source_bin: marker.source_bin,
                        error,
                    })
                    throw error
                })
        )
    }

    getFreeSelectionsForCase(caseId: CaseId): Observable<FreeSelectionsForCase> {
        return this.http
            .get<{ [name: string]: SelectionJson }>(`${this.baseUrl}/datasets/${caseId}/selections`)
            .pipe(
                map((selectionsJson: SelectionsJson) => {
                    const selections = new Map<string, FreeSelection>()
                    const seenIds = new Set<string>()
                    const repaired: FreeSelection[] = []
                    for (const [name, selectionJson] of Object.entries(selectionsJson)) {
                        const selection = parseSelectionJsonToFreeSelection(selectionJson)
                        if (!selection.name) {
                            selection.name = name
                        }
                        // Trust the DB uuid. Only mint when missing or colliding with
                        // another free selection (legacy Save As bug), then persist.
                        if (!selection.id || seenIds.has(selection.id)) {
                            selection.id = generateSelectionId()
                            repaired.push(selection)
                        }
                        seenIds.add(selection.id)
                        selections.set(name, selection)
                    }
                    return { selections, repaired }
                }),
                switchMap(({ selections, repaired }) => {
                    if (repaired.length === 0) {
                        return of(selections)
                    }
                    return forkJoin(
                        repaired.map(selection => this.saveFreeSelection(caseId, selection))
                    ).pipe(map(() => selections))
                })
            )
    }

    getHierarchicalSelectionListForCase(caseId: CaseId): Observable<string[]> {
        return this.http.get<string[]>(`${this.baseUrl}/datasets/${caseId}/hierarchicalSelections`)
    }

    getSingleHierarchicalSelectionForCase(
        caseId: CaseId,
        selectionName: string
    ): Observable<HierarchicalSelectionsForCase> {
        return this.http
            .get<SelectionJson>(
                `${this.baseUrl}/datasets/${caseId}/hierarchicalSelections/${encodeURIComponent(
                    selectionName
                )}`
            )
            .pipe(
                switchMap((selectionsJson: SelectionJson) => {
                    const root = parseSelectionJsonToHierarchicalSelectionRoot(selectionsJson)
                    if (!root.name) {
                        root.name = selectionName
                    }
                    const needsPersist = ensureSelectionTreeIds(root)
                    const result = new Map<string, HierarchicalSelectionRoot>([
                        [selectionName, root],
                    ])
                    if (!needsPersist) {
                        return of(result)
                    }
                    return this.saveHierarchicalSelection(caseId, root).pipe(map(() => result))
                })
            )
    }

    getUnsupervisedSelectionListForCase(caseId: CaseId): Observable<string[]> {
        return this.http.get<string[]>(`${this.baseUrl}/datasets/${caseId}/unsupervisedSelections`)
    }

    getSingleUnsupervisedSelectionForCase(
        caseId: CaseId,
        selectionName: string
    ): Observable<HierarchicalSelectionsForCase> {
        return this.http
            .get<SelectionJson>(
                `${this.baseUrl}/datasets/${caseId}/unsupervisedSelections/${encodeURIComponent(
                    selectionName
                )}`
            )
            .pipe(
                switchMap((selectionsJson: SelectionJson) => {
                    const root = parseSelectionJsonToUnsupervisedSelectionRoot(selectionsJson)
                    if (!root.name) {
                        root.name = selectionName
                    }
                    const needsPersist = ensureSelectionTreeIds(root)
                    const result = new Map<string, HierarchicalSelectionRoot>([
                        [selectionName, root],
                    ])
                    if (!needsPersist) {
                        return of(result)
                    }
                    return this.saveUnsupervisedSelection(caseId, root).pipe(map(() => result))
                })
            )
    }

    saveFreeSelection(caseId: CaseId, selection: FreeSelection): Observable<unknown> {
        if (!selection.name) {
            throw new Error('Error saving selection, invalid selection name.')
        }
        const body: SelectionJson = parseFreeSelectionToSelectionJson(selection)
        return this.http.put<unknown>(
            `${this.baseUrl}/datasets/${caseId}/selections/${encodeURIComponent(selection.name)}`,
            body
        )
    }

    saveHierarchicalSelection(
        caseId: CaseId,
        selection: HierarchicalSelectionRoot
    ): Observable<unknown> {
        if (!selection.name) {
            throw new Error('Error saving selection, invalid selection name.')
        }
        const body: SelectionJson = parseHierarchicalSelectionRootToSelectionJson(selection)

        return this.http.put<unknown>(
            `${this.baseUrl}/datasets/${caseId}/hierarchicalSelections/${encodeURIComponent(
                selection.name
            )}`,
            body
        )
    }

    saveUnsupervisedSelection(
        caseId: CaseId,
        selection: HierarchicalSelectionRoot
    ): Observable<unknown> {
        if (!selection.name) {
            throw new Error('Error saving selection, invalid selection name.')
        }
        const body: SelectionJson = parseHierarchicalSelectionRootToSelectionJson(selection)

        return this.http.put<unknown>(
            `${this.baseUrl}/datasets/${caseId}/unsupervisedSelections/${encodeURIComponent(
                selection.name
            )}`,
            body
        )
    }

    renameFreeSelection(
        caseId: CaseId,
        selectionName: string,
        newName: string
    ): Observable<unknown> {
        return this.http.put<unknown>(
            `${this.baseUrl}/datasets/${caseId}/selections/${selectionName}/rename`,
            { newName }
        )
    }

    renameHierarchicalSelectionRoot(
        caseId: CaseId,
        selectionName: string,
        newName: string
    ): Observable<unknown> {
        return this.http.put<unknown>(
            `${this.baseUrl}/datasets/${caseId}/hierarchicalSelections/${encodeURIComponent(
                selectionName
            )}/rename`,
            { newName }
        )
    }

    renameHierarchicalSelection(
        caseId: CaseId,
        rootName: string,
        selectionName: string,
        newName: string
    ): Observable<unknown> {
        return this.http.put<unknown>(
            `${this.baseUrl}/datasets/${caseId}/hierarchicalSelections/${encodeURIComponent(
                rootName
            )}/selections/${encodeURIComponent(selectionName)}/rename`,
            { newName }
        )
    }

    renameUnsupervisedSelectionRoot(
        caseId: CaseId,
        selectionName: string,
        newName: string
    ): Observable<unknown> {
        return this.http.put<unknown>(
            `${this.baseUrl}/datasets/${caseId}/unsupervisedSelections/${encodeURIComponent(
                selectionName
            )}/rename`,
            { newName }
        )
    }

    renameUnsupervisedSelection(
        caseId: CaseId,
        rootName: string,
        selectionName: string,
        newName: string
    ): Observable<unknown> {
        return this.http.put<unknown>(
            `${this.baseUrl}/datasets/${caseId}/unsupervisedSelections/${encodeURIComponent(
                rootName
            )}/selections/${encodeURIComponent(selectionName)}/rename`,
            { newName }
        )
    }

    deleteFreeSelection(caseId: CaseId, selectionName: string): Observable<unknown> {
        return this.http.delete<unknown>(
            `${this.baseUrl}/datasets/${caseId}/selections/${encodeURIComponent(selectionName)}`
        )
    }

    deleteHierarchicalSelection(caseId: CaseId, selectionName: string): Observable<unknown> {
        return this.http.delete<unknown>(
            `${this.baseUrl}/datasets/${caseId}/hierarchicalSelections/${encodeURIComponent(
                selectionName
            )}`
        )
    }

    deleteUnsupervisedSelection(caseId: CaseId, selectionName: string): Observable<unknown> {
        return this.http.delete<unknown>(
            `${this.baseUrl}/datasets/${caseId}/unsupervisedSelections/${encodeURIComponent(
                selectionName
            )}`
        )
    }

    getSelectionsBlobForCase(caseId: CaseId): Observable<Blob> {
        return this.http.get(`${this.baseUrl}/datasets/${caseId}/selectionsDownload`, {
            responseType: 'blob',
        })
    }

    getHierarchicalSelectionsBlobForCase(caseId: CaseId, name: string): Observable<Blob> {
        return this.http.get(
            `${this.baseUrl}/datasets/${caseId}/hierarchicalSelectionsDownload/${name}`,
            {
                responseType: 'blob',
            }
        )
    }

    getUnsupervisedSelectionsBlobForCase(caseId: CaseId, name: string): Observable<Blob> {
        return this.http.get(
            `${this.baseUrl}/datasets/${caseId}/unsupervisedSelectionsDownload/${encodeURIComponent(
                name
            )}`,
            {
                responseType: 'blob',
            }
        )
    }

    getMarkersBlobForCase(caseId: CaseId, mapId: number | string): Observable<Blob> {
        const url = `${this.baseUrl}/datasets/${caseId}/mappings/${mapId}/download`
        return this.http.get(url, { responseType: 'blob' })
    }

    getRecurrentNeighborhoodsResults(caseId: CaseId): Observable<any> {
        return this.http.get(`${this.baseUrl}/datasets/${caseId}/jobs/rcn`)
    }

    getRecurrentNeighborhoodsResultBlob(url: string): Observable<Blob> {
        return this.http.get(url, {
            responseType: 'blob',
        })
    }

    getNeighborhoodEnrichmentResults(caseId: CaseId): Observable<any> {
        return this.http.get(`${this.baseUrl}/datasets/${caseId}/jobs/nhoodEnrichment`)
    }

    getNeighborhoodEnrichmentResultBlob(url: string): Observable<Blob> {
        return this.http.get(url, {
            responseType: 'blob',
        })
    }

    getTsneResults(caseId: CaseId): Observable<any> {
        return this.http.get(`${this.baseUrl}/datasets/${caseId}/jobs/tsne`)
    }

    getUmapResults(caseId: CaseId): Observable<any> {
        return this.http.get(`${this.baseUrl}/datasets/${caseId}/jobs/umap`)
    }

    getAutoCellDetectionResults(caseId: CaseId): Observable<any> {
        return this.http.get(`${this.baseUrl}/datasets/${caseId}/jobs/acd`)
    }

    getAutoCellDetectionResultBlob(url: string): Observable<Blob> {
        return this.http.get(url, {
            responseType: 'blob',
        })
    }

    getPlottingResults(caseId: CaseId): Observable<any> {
        return this.http.get(`${this.baseUrl}/datasets/${caseId}/jobs/plotting`)
    }

    deletePlottingResult(caseId: CaseId, name: string): Observable<any> {
        return this.http.delete(`${this.baseUrl}/datasets/${caseId}/jobs/plotting/${name}`)
    }

    getSegmentColors(colorSeed: number, segmentIds: number[]): Observable<string[]> {
        return this.http.put<string[]>(`${this.baseUrl}/segmentColors`, {
            colorSeed,
            segmentIds,
        })
    }

    getMarkerMapping(caseId: CaseId): Observable<any> {
        return this.http.get(`${this.baseUrl}/datasets/${caseId}/markers/job_settings.json`).pipe(
            catchError((error: HttpErrorResponse) => {
                if (error.status === 404) {
                    return of({}) // Return an empty object if 404 error
                }
                return throwError(() => error) // Re-throw the error for other statuses
            })
        )
    }

    getHistogram(url: string): Observable<number[]> {
        return this.http.get(url + '/histogram.txt', { responseType: 'text' }).pipe(
            map((response: string) => {
                const values = response.split(';').map(val => parseInt(val.trim(), 10))
                return values.splice(2)
            })
        )
    }

    getSnapshotLink(
        caseId: CaseId,
        width: number,
        height: number,
        points: number[]
    ): Observable<{ url: string }> {
        return this.store.select(selectViewerState).pipe(
            take(1),
            switchMap(viewerState => {
                const body = {
                    currentViewstate: mapViewerStateToApiRequest(viewerState),
                    screenSize: [width, height],
                    points: points,
                }
                return this.http.post<{ url: string }>(
                    `${this.baseUrl}/datasets/${caseId}/snapshot`,
                    body
                )
            })
        )
    }

    getSnapshotBlob(url: string): Observable<Blob> {
        return this.http.get(url, {
            responseType: 'blob',
        })
    }

    saveViewerState<T>(): MonoTypeOperatorFunction<T> {
        return (source: Observable<T>): Observable<T> =>
            source.pipe(
                concatLatestFrom(() => this.store.select(selectCaseId)),
                concatLatestFrom(() => this.store.select(selectViewerState)),
                switchMap(([[originalValue, caseId], viewerState]) => {
                    if (caseId === null) return of(originalValue)
                    if (!isValidViewerState(viewerState)) return of(originalValue)
                    return this.postViewerState(
                        mapViewerStateToApiRequest(viewerState),
                        caseId
                    ).pipe(map(() => originalValue))
                })
            )
    }

    private postViewerState(config: ViewerConfigApiRequest, caseId: CaseId): Observable<unknown> {
        return this.http.post<unknown>(`${this.baseUrl}/datasets/${caseId}/state`, config)
    }

    getDatasetSharingLink(): Observable<{ url: string }> {
        return this.store.select(selectCaseId).pipe(
            take(1),
            this.saveViewerState(),
            switchMap(caseId =>
                this.http.post<{ url: string }>(`${this.baseUrl}/datasets/${caseId}/share`, {})
            )
        )
    }

    deleteLayer(name: string): Observable<any> {
        let uuid: string | undefined
        this.store
            .select(selectLayers)
            .pipe(take(1))
            .subscribe(layers => {
                uuid = layers?.find(l => l.name === name)?.id
            })
        return this.store.select(selectCaseId).pipe(
            take(1),
            this.saveViewerState(),
            switchMap(caseId =>
                this.http.delete(`${this.baseUrl}/layers/${caseId}`, {
                    body: {
                        layersId: [uuid],
                    },
                })
            )
        )
    }
}

function isValidViewerState(viewerState: ViewerState): boolean {
    if (viewerState.position === undefined) {
        console.error('bug: ignoring attempt to store undefined viewer state')
        return false
    }
    if (viewerState.crossSectionScale === 0) {
        // zoom 0 is guarded against in the store (viewerState), should never happen
        console.error('bug: ignoring attempt to store zoom 0 viewer state')
        return false
    }
    return true
}

function parseSelectionJsonToFreeSelection(selectionJson: SelectionJson): FreeSelection {
    return {
        // Keep server id as-is (possibly empty). Never invent here — that skipped persist.
        id: selectionJson.id ?? '',
        name: selectionJson.name,
        type: SelectionType.Free,
        buckets: parseBucketsFromJson(selectionJson.buckets),
        annotationVisible: selectionJson.annotationVisible,
        annotationColor: selectionJson.annotationColor,
        annotationOpacity: selectionJson.annotationOpacity,
        modified: false,
    }
}

function parseSelectionJsonToHierarchicalSelectionRoot(
    selectionJson: SelectionJson
): HierarchicalSelectionRoot {
    const children = selectionJson.children?.map(child =>
        parseSelectionJsonToHierarchicalSelectionNode(child)
    )
    const root: HierarchicalSelectionRoot = {
        id: selectionJson.id ?? '',
        name: selectionJson.name,
        type: SelectionType.Hierarchical,
        children,
        segSources: selectionJson.segSources ?? [],
        modified: false,
    }
    if (!root.segSources.length) {
        root.segSources = getHierarchicalRootSegSources(root)
    }
    return root
}

function parseSelectionJsonToHierarchicalSelectionNode(
    selectionJson: SelectionJson
): HierarchicalSelectionNode {
    return {
        id: selectionJson.id ?? '',
        name: selectionJson.name,
        buckets: parseBucketsFromJson(selectionJson.buckets),
        annotationVisible: selectionJson.annotationVisible,
        annotationColor: selectionJson.annotationColor,
        annotationOpacity: selectionJson.annotationOpacity,
        children: selectionJson.children?.map(child =>
            parseSelectionJsonToHierarchicalSelectionNode(child)
        ),
        modified: false,
    }
}

function parseSelectionJsonToUnsupervisedSelectionRoot(
    selectionJson: SelectionJson
): UnsupervisedSelectionRoot {
    const children = selectionJson.children?.map(child =>
        parseSelectionJsonToUnsupervisedSelectionNode(child)
    )
    const root: UnsupervisedSelectionRoot = {
        id: selectionJson.id ?? '',
        name: selectionJson.name,
        type: SelectionType.Unsupervised,
        children,
        segSources: selectionJson.segSources ?? [],
        modified: false,
    }
    if (!root.segSources.length) {
        root.segSources = getHierarchicalRootSegSources(root)
    }
    return root
}

/** Mint ids only when missing/duplicate within the tree; returns true if any id changed. */
function ensureSelectionTreeIds(
    root: HierarchicalSelectionRoot | UnsupervisedSelectionRoot
): boolean {
    const seenIds = new Set<string>()
    let repaired = false
    const walk = (node: { id: string; children?: HierarchicalSelectionNode[] | undefined }) => {
        if (!node.id || seenIds.has(node.id)) {
            node.id = generateSelectionId()
            repaired = true
        }
        seenIds.add(node.id)
        node.children?.forEach(walk)
    }
    walk(root)
    return repaired
}

function parseSelectionJsonToUnsupervisedSelectionNode(
    selectionJson: SelectionJson
): HierarchicalSelectionNode {
    return parseSelectionJsonToHierarchicalSelectionNode(selectionJson)
}

function parseBucketsFromJson(buckets: SelectionBucketJson[] | undefined): SelectionBucket[] {
    return (buckets ?? []).map(b => ({
        segSource: b.segSource,
        points: new Set(b.points),
    }))
}

function parseBucketsToJson(buckets: SelectionBucket[]): SelectionBucketJson[] {
    return buckets.map(b => ({
        segSource: b.segSource,
        points: Array.from(b.points),
    }))
}

function parseFreeSelectionToSelectionJson(selection: FreeSelection): SelectionJson {
    return {
        schemaVersion: SELECTION_SCHEMA_VERSION,
        id: selection.id,
        name: selection.name,
        buckets: parseBucketsToJson(selection.buckets),
        annotationVisible: selection.annotationVisible,
        annotationColor: selection.annotationColor,
        annotationOpacity: selection.annotationOpacity,
        children: undefined,
    }
}

function parseHierarchicalSelectionRootToSelectionJson(
    selection: HierarchicalSelectionRoot
): SelectionJson {
    return {
        schemaVersion: SELECTION_SCHEMA_VERSION,
        id: selection.id,
        name: selection.name,
        buckets: [],
        annotationVisible: false,
        annotationColor: '#ffffff',
        annotationOpacity: 1,
        segSources: getHierarchicalRootSegSources(selection),
        children: selection.children?.map(child =>
            parseHierarchicalSelectionNodeToSelectionJson(child)
        ),
    }
}

function parseHierarchicalSelectionNodeToSelectionJson(
    selection: HierarchicalSelectionNode
): SelectionJson {
    return {
        schemaVersion: SELECTION_SCHEMA_VERSION,
        id: selection.id,
        name: selection.name,
        buckets: parseBucketsToJson(selection.buckets),
        annotationVisible: selection.annotationVisible,
        annotationColor: selection.annotationColor,
        annotationOpacity: selection.annotationOpacity,
        children: selection.children?.map(child =>
            parseHierarchicalSelectionNodeToSelectionJson(child)
        ),
    }
}

interface SelectionJson {
    schemaVersion: number
    id?: string
    name: string | undefined
    buckets: SelectionBucketJson[]
    annotationVisible: boolean
    annotationColor: string
    annotationOpacity: number
    children: SelectionJson[] | undefined
    /** Present on hierarchical/unsupervised roots; locked intensity-map footprint. */
    segSources?: string[]
}

interface SelectionsJson {
    [name: string]: SelectionJson
}
